import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './lib/auth/AuthContext';
import { ToastProvider } from './components/ui/Toast';
import { WorkspaceParamSync } from './components/layout/WorkspaceParamSync';
import { RequireAuth } from './components/auth/RequireAuth';
import { EpicDashboardPage } from './pages/index';
import { EpicDetailPage } from './pages/epics/[id]/page';
import { NewEpicPage } from './pages/epics/new';
import { LoginPage } from './pages/login';
import { SignupPage } from './pages/signup';
import { ConfirmPage } from './pages/confirm';
import { TodayPage } from './pages/today';
import { InsightsHubPage } from './pages/insights/index';
import { RedirectWithParams } from './components/routing/RedirectWithParams';
import {
  healthRedirectTransform,
  spendRedirectTransform,
  efficiencyRedirectTransform,
} from './lib/insightsRedirects';
import { OnboardPage } from './pages/onboard/index';
import { AccountMembersPage } from './pages/account/members';
import { AccountPlanPage } from './pages/account/plan';
import { ProjectsListPage } from './pages/projects/index';
import { NewProjectPage } from './pages/projects/new';
import { ProjectDetailPage } from './pages/projects/[id]';
import { ReposListPage } from './pages/repos/index';
import { RepoSkillsPage } from './pages/repos/[org]/[repo]/skills';
import { SessionLogsPage } from './pages/session-logs';
import { FlagsAdminPage } from './pages/admin/flags';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

/** Root application: providers + router. */
export const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <AuthProvider>
            <WorkspaceParamSync />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/confirm" element={<ConfirmPage />} />
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <EpicDashboardPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/epics/new"
                element={
                  <RequireAuth>
                    <NewEpicPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/epics/:id"
                element={
                  <RequireAuth>
                    <EpicDetailPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/today"
                element={
                  <RequireAuth>
                    <TodayPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/insights"
                element={
                  <RequireAuth>
                    <InsightsHubPage />
                  </RequireAuth>
                }
              />
              {/* UX-02 — Health, Spend and Efficiency became tabs of the
                  /insights hub; these old URLs still work as redirects. */}
              <Route
                path="/health"
                element={
                  <RequireAuth>
                    <RedirectWithParams to="/insights" transform={healthRedirectTransform} />
                  </RequireAuth>
                }
              />
              <Route
                path="/reports/spend"
                element={
                  <RequireAuth>
                    <RedirectWithParams to="/insights" transform={spendRedirectTransform} />
                  </RequireAuth>
                }
              />
              <Route
                path="/analytics/cost-efficiency"
                element={
                  <RequireAuth>
                    <RedirectWithParams to="/insights" transform={efficiencyRedirectTransform} />
                  </RequireAuth>
                }
              />
              <Route
                path="/onboard"
                element={
                  <RequireAuth allowWithoutAccount>
                    <OnboardPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/account/members"
                element={
                  <RequireAuth>
                    <AccountMembersPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/account/plan"
                element={
                  <RequireAuth>
                    <AccountPlanPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/projects"
                element={
                  <RequireAuth>
                    <ProjectsListPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/projects/new"
                element={
                  <RequireAuth>
                    <NewProjectPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/projects/:id"
                element={
                  <RequireAuth>
                    <ProjectDetailPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/repos"
                element={
                  <RequireAuth>
                    <ReposListPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/repos/:org/:repo/skills"
                element={
                  <RequireAuth>
                    <RepoSkillsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/session-logs"
                element={
                  <RequireAuth>
                    <SessionLogsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/flags"
                element={
                  <RequireAuth>
                    <FlagsAdminPage />
                  </RequireAuth>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
};

export default App;
